import {
  DeepLinkService,
  IPhoto,
  IRideStep,
  PhotosDialog,
  Poi,
  PoiService,
  Ride,
  RideService,
  Route,
  RouteService,
  useCancellablePromise,
  useTracker,
  useUnits,
} from '@geovelo-frontends/commons';
import { Box, ButtonBase, Card, CardContent, Skeleton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { graphql, navigate } from 'gatsby';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RideQuery } from '../../../graphql-types';
import defaultRideImage from '../../assets/images/ride.jpg';
import Button from '../../components/button';
import ConfirmDialog from '../../components/confirm-dialog';
import { DiscoverIcon, PhoneIcon, RemoveIcon, ThumbUpIcon } from '../../components/icons';
import Seo from '../../components/seo';
import { maxWidth } from '../../components/slices/consts';
import { AppContext, defaultSmallDeviceShowDetailsAction } from '../../context';
import Footer from '../../layouts/default/footer';
import { TPageProps } from '../../page-props';
import { parseRide } from '../../utils/ride';

import Breadcrumb from './breadcrumb';
import Creator from './creator';
import Data from './data';
import ElevationProfile from './elevation-profile';
import Header from './header';
import Map from './map';
import Photo from './photo';
import PointsOfInterest from './points-of-interest';
import Report from './report';
import RoadTypes from './road-types';

function RideDetailsPage(props: TPageProps<RideQuery>) {
  const {
    location: { pathname },
    data: { ride: _ride },
    pageContext: { id: rideId },
    params: { id: _rideId },
  } = props;
  const [partnersGeogroups] = useState(() =>
    props.data.allGeogroupWithRides.nodes.reduce<{ [key: number]: { id: number } }>(
      (res, { data }) => {
        if (data?.id && data.partner?.id) res[data.partner.id] = { id: data.id };
        return res;
      },
      [],
    ),
  );
  const [loadingRide] = useState<Ride | undefined | null>(
    (_ride?.data && parseRide(_ride.data)) || undefined,
  );
  const [ride, setRide] = useState<Ride | undefined | null>();
  const [route, setRoute] = useState<Route | null>();
  const [returnRoute, setReturnRoute] = useState<Route | null>();
  const [pois, setPois] = useState<Poi[]>();
  const [showedPhotos, showPhotos] = useState<IPhoto[] | null>(null);
  const [photosDialogOpen, openPhotosDialog] = useState(false);
  const [removeDialogOpen, openRemoveDialog] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const {
    app: { smallDeviceRedirectAction },
    user: { current: currentUser, rides: userRides },
    poi: { categories: poiCategories },
    actions: { setSmallDeviceShowDetailsAction, setSmallDeviceRedirectAction, getUserRides },
  } = useContext(AppContext);
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const { cancellablePromise, cancelPromises } = useCancellablePromise();
  const { trackEvent } = useTracker();
  const { toDistance, toTime } = useUnits();

  useEffect(() => {
    setInitialized(true);

    setSmallDeviceShowDetailsAction({
      labelKey: 'commons.actions.see_details',
      Icon: DiscoverIcon,
    });

    return () => {
      setSmallDeviceShowDetailsAction(defaultSmallDeviceShowDetailsAction);
    };
  }, []);

  useEffect(() => {
    if (initialized && (rideId || _rideId)) getRide(rideId || parseInt(_rideId));

    return () => {
      cancelPromises();
    };
  }, [initialized]);

  useEffect(() => {
    if (currentUser) {
      if (!userRides) getUserRides();
    }
  }, [currentUser]);

  useEffect(() => {
    if (poiCategories && ride) getPois();
  }, [poiCategories, ride]);

  useEffect(() => {
    if (ride) {
      getRoute();

      const deepLinkCallback = DeepLinkService.getCallback({
        androidLink: `france/rides/${ride.id}`,
        iosLink: `rides/${ride.id}`,
      });

      if (deepLinkCallback) {
        setSmallDeviceRedirectAction({
          labelKey: 'geovelo.actions.navigate_on_app',
          callback: () => {
            trackEvent('General stores button', 'Clicked', 'Navigation redirection ride');
            deepLinkCallback;
          },
        });
      }
    } else if (ride === null) navigate(`/${currentLanguage}/rides`);

    return () => {
      setSmallDeviceRedirectAction();
    };
  }, [ride]);

  async function getRoute() {
    setRoute(undefined);
    setReturnRoute(undefined);

    if (!ride) return;

    try {
      const [_route, _returnRoute] = await cancellablePromise(
        Promise.all([
          ride.routeId ? RouteService.getRoute(ride.routeId) : null,
          ride.returnRouteId ? RouteService.getRoute(ride.returnRouteId) : null,
        ]),
      );

      setRoute(_route);
      setReturnRoute(_returnRoute);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        setRoute(null);
        setReturnRoute(null);
      }
    }
  }

  async function getRide(id: number) {
    try {
      const ride = await cancellablePromise(RideService.getRide(id));

      setRide(ride);
    } catch (err) {
      if (
        !(err instanceof Error) ||
        err?.name !== 'CancelledPromiseError' ||
        (typeof err === 'object' && err && 'detail' in err)
      ) {
        console.error(err);
        setRide(null);
      }
    }
  }

  async function getPois() {
    if (!poiCategories || !ride) return;

    if (ride.poiIds.length === 0) {
      setPois([]);
      return;
    }

    try {
      const _pois = await cancellablePromise(
        PoiService.getPois(poiCategories, { ids: ride.poiIds }),
      );

      setPois(_pois);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') console.error(err);
    }
  }

  function handlePhotosDialogOpen() {
    if (!ride || ride.photos.length === 0) return;
    showPhotos(ride.photos);
    openPhotosDialog(true);
  }

  function handleStepPhotosDialogOpen(step: IRideStep) {
    if (step.photos.length === 0) return;
    showPhotos(step.photos);
    openPhotosDialog(true);
  }

  function handlePhotosDialogClose() {
    openPhotosDialog(false);
    setTimeout(() => showPhotos(null), theme.transitions.duration.leavingScreen);
  }

  async function handleRemove(confirmed: boolean) {
    if (confirmed && ride?.id) {
      setRemoving(true);

      try {
        await RideService.removeRide(ride.id);

        enqueueSnackbar(t('geovelo.rides.detail.remove_dialog.success'));
        navigate(`/${currentLanguage}/rides`);
      } catch (err) {
        enqueueSnackbar(t('geovelo.rides.detail.remove_dialog.server_error'), { variant: 'error' });
      }

      setRemoving(false);
    } else {
      openRemoveDialog(false);
    }
  }

  if (ride === null) return <></>;

  const partner = ride?.partners?.filter(({ code }) => code !== 'geovelo')?.[0];
  const isOwner =
    (currentUser &&
      ride?.creator &&
      (!ride.partners || ride.partners.length === 0) &&
      currentUser.id === ride.creator.id) ||
    false;
  const rideOrLoadingRide = ride || loadingRide;
  const description =
    rideOrLoadingRide?.metaDescription ||
    (rideOrLoadingRide &&
      `${toDistance(rideOrLoadingRide.distance || 0).replace(/\u00a0/g, ' ')} | ${toTime(
        rideOrLoadingRide.duration || 0,
      ).replace(/\u00a0/g, ' ')}${
        rideOrLoadingRide.description ? ` | ${rideOrLoadingRide.description}` : ''
      }`) ||
    '';

  return (
    <>
      {rideOrLoadingRide ? (
        <Seo
          description={
            description.length > 300 ? `${description.substring(0, 297)}...` : description
          }
          imageUrl={rideOrLoadingRide.photos[0]?.thumbnailUrl || defaultRideImage}
          title={`${
            rideOrLoadingRide.metaTitle
              ? rideOrLoadingRide.metaTitle
              : `${rideOrLoadingRide.title}${
                  rideOrLoadingRide.from?.addressDetail?.city
                    ? ` - ${t('geovelo.rides.detail.title_city', {
                        city: rideOrLoadingRide.from.addressDetail?.city,
                      })}`
                    : ''
                }`
          } - ${t('geovelo.rides.detail.title')}`}
          {...props}
        />
      ) : (
        <Seo title={`${t('geovelo.rides.detail.title')}`} {...props} />
      )}
      <Box flexGrow={1} sx={{ overflowY: 'auto' }}>
        <Box
          display="flex"
          flexDirection="column"
          gap={5}
          marginX="auto"
          maxWidth="calc(100% - 32px)"
          paddingY={2}
          width={maxWidth}
        >
          <Breadcrumb ride={ride} />
          <Header description={description} path={pathname} ride={ride} />
          <Photo isOwner={isOwner} onPhotosDialogOpen={handlePhotosDialogOpen} ride={ride} />
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={5}>
            <Box
              display="flex"
              flexDirection="column"
              flexShrink={0}
              gap={2}
              maxWidth="100%"
              width={{ xs: '100%', md: 379 }}
            >
              {partner && (
                <Card
                  sx={{ backgroundColor: '#eef9f4', borderColor: '#bfe3d4', borderRadius: 4 }}
                  variant="outlined"
                >
                  <CardContent
                    sx={{ '&&': { alignItems: 'center', display: 'flex', gap: 2, padding: 2 } }}
                  >
                    <ThumbUpIcon sx={{ color: '#2ac682' }} />
                    <Typography>{t('geovelo.rides.detail.certified')}</Typography>
                  </CardContent>
                </Card>
              )}
              <Data isOwner={isOwner} ride={ride} setRide={setRide} />
              <Creator partnersGeogroups={partnersGeogroups} ride={ride} />
              {isOwner ? (
                <Button
                  onClick={() => openRemoveDialog(true)}
                  size="small"
                  startIcon={<RemoveIcon color="action" fontSize="small" />}
                >
                  <Typography color="textSecondary" sx={{ textDecoration: 'underline' }}>
                    {t('geovelo.rides.detail.actions.remove')}
                  </Typography>
                </Button>
              ) : (
                <Report path={pathname} />
              )}
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              flexGrow={1}
              gap={5}
              sx={{ overflowX: 'hidden' }}
            >
              <Map
                onStepPhotosDialogOpen={handleStepPhotosDialogOpen}
                path={pathname}
                pois={pois}
                returnRoute={returnRoute}
                ride={ride}
                route={route}
              />
              {(!ride || ride.description) && (
                <Box
                  component={isOwner ? ButtonBase : 'div'}
                  justifyContent="stretch"
                  onClick={() =>
                    isOwner && ride && navigate(`/${currentLanguage}/ride-update/${ride.id}`)
                  }
                >
                  <Typography>
                    {ride?.description || (
                      <>
                        <Skeleton variant="text" width="100%" />
                        <Skeleton variant="text" width="50%" />
                      </>
                    )}
                  </Typography>
                </Box>
              )}
              <ElevationProfile route={route} />
              <RoadTypes route={route} />
              <PointsOfInterest
                isOwner={isOwner}
                onStepPhotosDialogOpen={handleStepPhotosDialogOpen}
                ride={ride}
              />
              <Box alignSelf="center" display={{ xs: 'block', md: 'none' }} marginTop={1}>
                {isOwner ? (
                  <Button
                    onClick={() => openRemoveDialog(true)}
                    size="small"
                    startIcon={<RemoveIcon color="action" fontSize="small" />}
                  >
                    <Typography color="textSecondary" sx={{ textDecoration: 'underline' }}>
                      {t('geovelo.rides.detail.actions.remove')}
                    </Typography>
                  </Button>
                ) : (
                  <Report path={pathname} />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        <Footer />
      </Box>
      <PhotosDialog
        onClose={handlePhotosDialogClose}
        open={photosDialogOpen}
        photos={showedPhotos}
      />
      <ConfirmDialog
        dangerous
        cancelLabel={t('geovelo.rides.detail.remove_dialog.cancel')}
        confirmLabel={t('geovelo.rides.detail.remove_dialog.confirm')}
        loading={removing}
        onClose={handleRemove}
        open={removeDialogOpen}
        title={t('geovelo.rides.detail.remove_dialog.title')}
      />
      {smallDeviceRedirectAction && (
        <Box
          bottom={56}
          display="flex"
          justifyContent="center"
          left={0}
          position="absolute"
          right={0}
        >
          <Button
            color="primary"
            onClick={smallDeviceRedirectAction.callback}
            size="medium"
            startIcon={<PhoneIcon />}
            sx={{ zIndex: 1200 }}
            variant="contained"
          >
            {t(smallDeviceRedirectAction.labelKey)}
          </Button>
        </Box>
      )}
    </>
  );
}

export const query = graphql`
  fragment RideData on rideData {
    description
    meta_description
    distance
    duration
    geo_point_a {
      coordinates
      type
    }
    geo_point_a_title
    id
    photos {
      id
      thumbnail
      url
    }
    title
    meta_title
  }
`;

export default RideDetailsPage;
